import React, {useState, useEffect, useRef} from 'react';
import Spinner from '@/components/ui/Spinner';
import {BaseModal} from '@/components/modals';
import {useAlert} from '@/components/alerts';
import EntitySelector from '@/components/navigation/EntitySelector';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';

export interface AgentTeamAssignmentAddModalProps {
    show: boolean;
    onSave: (assignmentData: any) => void;
    onClose: () => void;
    saving: boolean;
}

const AgentTeamAssignmentAddModal: React.FC<AgentTeamAssignmentAddModalProps> = ({
    show,
    onSave,
    onClose,
    saving
}) => {
    const {showError} = useAlert();
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const teamRestricted = restrictedFieldKeys.includes('team_pii');
    const [formData, setFormData] = useState({
        teamId: '',
        startDate: '',
        endDate: '',
        isActive: false
    });
    const prevShowRef = useRef(false);

    useEffect(() => {
        if (show && !prevShowRef.current) {
            setFormData({
                teamId: '',
                startDate: '',
                endDate: '',
                isActive: false
            });
        }
        prevShowRef.current = show;
    }, [show]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const nativeEvent = e.nativeEvent as SubmitEvent;
        const submitter = nativeEvent.submitter as HTMLElement | null;
        
        if (submitter) {
            const submitButton = document.querySelector('button[form="add-team-assignment-form"][type="submit"]') as HTMLButtonElement;
            if (submitButton && submitter !== submitButton && !submitButton.contains(submitter)) {
                return;
            }
        }

        if (!formData.teamId || !formData.startDate) {
            showError(i18n.t('jt_team_and_start_date_required.text', 'Team and start date are required'));
            return;
        }

        onSave(formData);
    };

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <BaseModal
            isOpen={show}
            onClose={onClose}
            title={i18n.t('jt_add_team_assignment.text', 'Add Team Assignment')}
            size="lg"
            footer={
                <div className="d-flex">
                    <button
                        type="button"
                        className="btn btn-secondary m-2"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {i18n.t('jt_cancel.text', 'Cancel')}
                    </button>
                    <button
                        type="submit"
                        form="add-team-assignment-form"
                        className="btn btn-primary m-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <Spinner variant="border" size="sm" className="mr-2" text={i18n.t('sp_saving.text')} centered />
                            </>
                        ) : (
                            i18n.t('jt_add_assignment.text', 'Add Assignment')
                        )}
                    </button>
                </div>
            }
        >
            <form id="add-team-assignment-form" onSubmit={handleSubmit} noValidate>
                <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                    <label htmlFor="teamId" className="form-label">
                        {i18n.t('th_team.text', 'Team')} *
                    </label>
                    <div onClick={(e) => e.stopPropagation()}>
                        {teamRestricted ? (
                            <RestrictedFieldInput />
                        ) : (
                            <EntitySelector
                                entityType="teams"
                                currentEntityId={formData.teamId ? parseInt(formData.teamId) : undefined}
                                onEntityChange={(teamId, teamName) => {
                                    handleInputChange('teamId', teamId.toString());
                                }}
                            />
                        )}
                    </div>
                </div>

                <div className="mb-3">
                    <label htmlFor="startDate" className="form-label">
                        {i18n.t('th_start_date.text', 'Start Date')} *
                    </label>
                    <input
                        type="date"
                        className="form-control"
                        id="startDate"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <label htmlFor="endDate" className="form-label">
                        {i18n.t('th_end_date.text', 'End Date')}
                    </label>
                    <input
                        type="date"
                        className="form-control"
                        id="endDate"
                        value={formData.endDate}
                        onChange={(e) => handleInputChange('endDate', e.target.value)}
                    />
                    <div className="form-text">
                        {i18n.t('jt_leave_empty_for_ongoing.text', 'Leave empty for ongoing assignment')}
                    </div>
                </div>

                <div className="mb-3">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="isActive"
                            checked={formData.isActive}
                            onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="isActive">
                            {i18n.t('jt_active_assignment.text', 'Active Assignment')}
                        </label>
                    </div>
                    <div className="form-text">
                        {i18n.t('jt_only_one_active_assignment.text', 'Only one assignment can be active at a time')}
                    </div>
                </div>
            </form>
        </BaseModal>
    );
};

export default AgentTeamAssignmentAddModal;
